import React from 'react'
import { useParams } from 'react-router-dom'
import ShareInviteCard from './components/ShareInviteCard'
import SpeakToKitLogo from './components/SpeakToKitLogo'
import { useShareInvite } from './hooks/useShareInvite'
import useSpeakToKitPageTitle from './hooks/useSpeakToKitPageTitle'
import { Pages } from '@/Pages'
import { speakToKitThemeVars } from './theme/speakToKitThemeVars'

const DEFAULT_SHARE_BASE =
    (import.meta.env.VITE_JOBSEEKER_SHARE_URL as string | undefined)?.trim() ??
    'https://jobseeker.contxt.network'

export default function ShareInviteFallback(): React.JSX.Element {
    useSpeakToKitPageTitle('Share Invite')
    const { referralCode } = useParams<{ referralCode: string }>()
    const normalizedCode = referralCode?.trim() ?? ''
    const landingPath = Pages.CandidateLandingOption1

    const {
        shareInviteUrl,
        whatsAppShareUrl,
        isMobileShareAvailable,
        isDesktopViewport,
        hasCopiedLink,
        handleCopyInvite,
        handleShareInvite,
    } = useShareInvite({
        landingPath,
        messageBuilder: (url) =>
            `Hey! I just had a great chat with Kit about finding my ideal work environment - it was genuinely useful. You should try it: ${url}`,
        fallbackBaseUrl: DEFAULT_SHARE_BASE,
        overrideShareOrigin: DEFAULT_SHARE_BASE,
        useEnvShareUrl: false,
        transformShareUrl: (baseUrl) => {
            if (!normalizedCode) {
                return baseUrl
            }
            try {
                const origin =
                    typeof window !== 'undefined'
                        ? window.location?.origin
                        : undefined
                const url = new URL(
                    baseUrl,
                    baseUrl.startsWith('http') ? undefined : origin,
                )
                url.searchParams.set('ref', normalizedCode)
                return url.toString()
            } catch {
                const separator = baseUrl.includes('?') ? '&' : '?'
                return `${baseUrl}${separator}ref=${encodeURIComponent(
                    normalizedCode,
                )}`
            }
        },
    })

    return (
        <div
            className="min-h-screen bg-background text-foreground flex flex-col font-sans antialiased"
            style={speakToKitThemeVars}
        >
            <header className="w-full py-6">
                <div className="mx-auto flex w-full max-w-[1200px] items-center justify-center px-6">
                    <SpeakToKitLogo to={Pages.CandidateLandingOption2} />
                </div>
            </header>
            <main className="flex-1 py-8 md:py-12">
                <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center px-6">
                    {normalizedCode ? (
                        <ShareInviteCard
                            shareInviteUrl={shareInviteUrl}
                            whatsAppShareUrl={whatsAppShareUrl}
                            isMobileShareAvailable={isMobileShareAvailable}
                            isDesktopViewport={isDesktopViewport}
                            hasCopiedLink={hasCopiedLink}
                            onCopy={handleCopyInvite}
                            onShare={handleShareInvite}
                            title="Share your Kit invite!"
                            body="Send your personalized link to someone who should try Kit."
                        />
                    ) : (
                        <div className="rounded-2xl border border-border bg-card p-6 text-center text-muted-foreground shadow-[var(--shadow-card)] max-w-2xl">
                            No referral code was provided. Please use the link
                            from your share invite message.
                        </div>
                    )}
                </div>
            </main>
        </div>
    )
}
